import { readonly, ref, useContext } from '@nuxtjs/composition-api';

import { withCheckoutCartWrite } from '~/helpers/checkout/withCartWrite';
import { Logger } from '~/helpers/logger';
import { saveBillingAddressCommand } from '~/modules/checkout/composables/useBilling/commands/saveBillingAddressCommand';
import useCart from '~/modules/checkout/composables/useCart';
import { CartContextChangedError, cartReadContractKey } from '~/modules/checkout/composables/useCart/cartCoordinator';
import { BillingCartAddress, Maybe } from '~/modules/GraphQL/types';

import type { UseBillingError, UseBillingInterface, UseBillingLoadParams, UseBillingSaveParams } from './useBilling';

/**
 * Allows loading and saving billing information
 * of the current cart.
 *
 * See the {@link UseBillingInterface} for a list of methods and values available in this composable.
 */
export function useBilling(): UseBillingInterface {
  const context = useContext();
  const { cart, load: loadCart, setCart, isCurrentData, lastFullReadState, error: cartError } = useCart();
  const owner = () => {
    const { state } = context.app.$vsf.$magento.config;
    return [state.getCartId?.() || '', state.getCustomerToken?.() || '', state.getStore?.() || '',
      state.getLocale?.() || '', state.getCurrency?.() || ''];
  };

  const loading = ref(false);
  let loadSequence = 0;
  const error = ref<UseBillingError>({
    load: null,
    save: null,
  });

  const load = async ({ customQuery = null, customHeaders = {} }: UseBillingLoadParams = {}): Promise<Maybe<BillingCartAddress>> => {
    Logger.debug('useBilling.load');
    let billingInfo = null;
    const sequence = ++loadSequence;
    const requestedOwner = owner();
    const readContract = { customQuery, customHeaders };
    const requestedContract = cartReadContractKey(readContract);

    try {
      loading.value = true;
      const customRead = Object.keys(customQuery ?? {}).length > 0 || Object.keys(customHeaders ?? {}).length > 0;
      if (!cart?.value?.billing_address || !isCurrentData?.() || customRead) {
        await loadCart({ customQuery, customHeaders, reuseExisting: true });
        if (lastFullReadState?.value !== 'applied' || !isCurrentData?.(readContract)) {
          throw cartError?.value?.load ?? new CartContextChangedError();
        }
      }
      await Promise.resolve();
      const currentOwner = owner();
      if (!isCurrentData?.(readContract) || requestedContract !== cartReadContractKey(readContract)
        || requestedOwner.some((value, index) => (index !== 0 || value !== '') && value !== currentOwner[index])) {
        throw new CartContextChangedError();
      }

      billingInfo = cart?.value?.billing_address ?? null;
      if (sequence === loadSequence) error.value.load = null;
    } catch (err) {
      if (sequence === loadSequence) error.value.load = err;
      Logger.error('useBilling/load', err);
    } finally {
      if (sequence === loadSequence) loading.value = false;
    }

    return billingInfo;
  };

  const save = async ({
    billingDetails,
    customQuery = { setBillingAddressOnCart: context.app.$vsf.$magento.config.state.getLocale() === 'cn' ? 'cart-cn-checkout-set-billing-address' : 'cart-checkout-set-billing-address' },
    customHeaders = {},
  }: UseBillingSaveParams): Promise<Maybe<BillingCartAddress>> => {
    Logger.debug('useBilling.save');
    let billingInfo = null;

    try {
      loading.value = true;
      const cartId = cart.value.id;
      billingInfo = await withCheckoutCartWrite(context.app, cartId, async (guard) => {
        const confirmed = await saveBillingAddressCommand.execute(context, cartId, billingDetails, customQuery, customHeaders);
        guard.assertCurrent();
        if (!confirmed) throw new Error('Error with set billing address');
        setCart({ ...cart.value, ...confirmed }, { customHeaders });
        return confirmed.billing_address;
      });

      error.value.save = null;
    } catch (err) {
      error.value.save = err;
      Logger.error('useBilling/save', err);
      throw err;
    } finally {
      loading.value = false;
    }

    return billingInfo;
  };

  return {
    load,
    save,
    error: readonly(error),
    loading: readonly(loading),
  };
}

export * from './useBilling';
export default useBilling;
